import type { ClassroomKnowledgeBlock, ClassroomSessionDocument } from '../../types/sync';

/** 对齐 iOS TranscriptContentMeasure：空格语言按词折算，中日韩仍按字计。 */
export function contentUnits(text: string, language: string) {
  if (!/^(en|fr|es|de|it|pt|nl|ru|uk|pl|tr|id|vi)/i.test(language)) return Array.from(text.replace(/\s/g, '')).length;
  const cjk = /[\u2e80-\u2fff\u3040-\u30ff\u3400-\u9fff\uac00-\ud7af\uf900-\ufaff]/g;
  return (text.match(cjk)?.length ?? 0) + (text.replace(cjk, ' ').match(/[\p{L}\p{N}]+(?:['’-][\p{L}\p{N}]+)*/gu)?.length ?? 0) * 2;
}

/** 长文本来源先分为可恢复的独立片段；拼接后的原文逐字不变。 */
export function splitSourceText(text: string, language: string): string[] {
  const chunks: string[] = [];
  while (text.length) {
    let low = 1, high = Math.min(text.length, 20_000);
    while (low < high) {
      const middle = Math.ceil((low + high) / 2);
      if (contentUnits(text.slice(0, middle), language) <= 600) low = middle;
      else high = middle - 1;
    }
    let end = low;
    if (end < text.length) {
      const candidates = [...text.slice(0, end).matchAll(/[。！？.!?；;\s]/g)];
      const boundary = candidates.at(-1)?.index;
      if (boundary !== undefined && boundary >= end / 2) end = boundary + 1;
      if (/^[\uDC00-\uDFFF]$/.test(text[end] ?? '')) end -= 1;
    }
    chunks.push(text.slice(0, end)); text = text.slice(end);
  }
  return chunks;
}

/** 浏览器没有原生 VAD：只使用最终落段时间、标点及内容硬顶，不把 partial 停滞伪装成声学停顿。 */
export function pendingWindow(session: ClassroomSessionDocument, flush: boolean, now = Date.now()) {
  const pending = session.transcriptSegments.filter(segment => segment.isFinalized && segment.processingState === 'pending' && segment.text.trim() && segment.speakerLabel !== 'source:photo:processing').sort((a, b) => a.startTime - b.startTime);
  if (!pending.length) return [];
  let units = 0, chars = 0;
  const selected: typeof pending = [];
  for (const segment of pending) {
    if (selected.length && (units + contentUnits(segment.text, session.transcriptLanguage) > 600 || chars + segment.text.length + 1 > 20_000)) break;
    if (segment.text.length > 20_000) throw new Error('单个来源超过处理上限，请拆分后再整理；原文已保留。');
    selected.push(segment); units += contentUnits(segment.text, session.transcriptLanguage); chars += segment.text.length + 1;
    if (units >= 600) break;
  }
  const last = selected.at(-1)!;
  const age = (now - Date.parse(pending[0]!.createdAt)) / 1000;
  const silence = (now - Date.parse(last.createdAt)) / 1000;
  if (flush || units >= 600 || (age >= 150 && units >= 16) || (units >= 80 && silence >= 12 && /[。！？.!?]$/.test(last.text.trim()))) return selected;
  return [];
}

const administrative = ['测试一下麦克风','测试麦克风','测试录音','录音测试','声音测试','测试声音','能听到我说话','能听见我说话','听得到吗','听得见吗','声音能听清','麦克风有没有声音','准备开始上课','还没有正式开始','还没开始讲课','我们先点名','现在开始点名','同学们大家好','同學們大家好','大家先安静','等同学到齐','稍等一下再开始','microphonecheck','testingthemicrophone','testingthemic','testrecording','soundcheck','canyouhearme','beforewestartclass','waitingforeveryone','takingattendance','testdumicro','vousmentendez','pruebademicrófono','meescuchan','mikrofontest','könnensiemichhören','マイクテスト','聞こえますか','마이크테스트','제목소리들리세요'];
const identity = (text: string) => text.toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');
function meaningful(text: string, language: string) {
  return text.slice(0, 12_000).split(/[。！？!?；;\n]+|\.(?=\s|$)/).filter(sentence => {
    const key = identity(sentence);
    if (key.length < 12 || new Set(key).size < 8 || contentUnits(sentence, language) < 12) return false;
    if (key.length <= 100 && administrative.some(phrase => key.includes(phrase))) return false;
    const triples = Array.from({ length: Math.max(0, key.length - 2) }, (_, i) => key.slice(i, i + 3));
    return key.length <= 100 || new Set(triples).size * 5 >= triples.length;
  });
}

/** 与 iOS 61dce26 相同：12–24 块、常规 900 单位，收尾 240，过滤试音与重复内容。 */
export function selectQuizBlocks(blocks: ClassroomKnowledgeBlock[], language: string, flush: boolean) {
  const seen = new Set(blocks.filter(block => block.processingStatus === 'done').sort((a, b) => a.orderIndex - b.orderIndex).slice(-64).flatMap(block => meaningful(block.sourceText, language).map(identity)));
  const selected: ClassroomKnowledgeBlock[] = [], skipped: string[] = [];
  let units = 0, sentences = 0;
  for (const block of blocks.filter(block => ['ready', 'quizPending'].includes(block.processingStatus)).sort((a, b) => a.orderIndex - b.orderIndex)) {
    const localSeen = new Set(seen);
    const fresh = meaningful(block.sourceText, language).filter(sentence => { const id = identity(sentence); if (localSeen.has(id)) return false; localSeen.add(id); return true; });
    const amount = fresh.reduce((sum, sentence) => sum + contentUnits(sentence, language), 0);
    if (amount < 40) { skipped.push(block.id); continue; }
    fresh.forEach(sentence => seen.add(identity(sentence)));
    selected.push(block); units += amount; sentences += fresh.length;
    if (selected.length >= 24 || (!flush && selected.length >= 12 && units >= 900)) break;
  }
  if (sentences < 2 || units < (flush ? 240 : 900) || (!flush && selected.length < 12)) {
    if (flush) skipped.push(...selected.map(block => block.id));
    return { selected: [], skipped };
  }
  return { selected, skipped };
}
